import type {
  WishlistActivityType,
  WishlistActivityViewModel,
} from '@nexus/customer/wishlists/model';

import type { WishlistActivityFeedItemView } from '../types/wishlist-activity-feed-item-view.interface';
import type { WishlistActivityFeedMode } from '../types/wishlist-activity-feed-mode.type';
import type { WishlistActivityFeedTone } from '../types/wishlist-activity-feed-tone.type';

interface WishlistActivityPresentation {
  readonly compactTitle: string;
  readonly managerTitle: string;
  readonly iconLabel: string;
  readonly tone: WishlistActivityFeedTone;
  readonly statusLabel: string | null;
}

const activityDateFormatter: Intl.DateTimeFormat = new Intl.DateTimeFormat('pl-PL', {
  day: 'numeric',
  month: 'short',
  hour: '2-digit',
  minute: '2-digit',
});

const ACTIVITY_PRESENTATION: Record<WishlistActivityType, WishlistActivityPresentation> = {
  wishlist_created: {
    compactTitle: 'Utworzono listę',
    managerTitle: 'Utworzono wishlistę',
    iconLabel: 'W',
    tone: 'success',
    statusLabel: null,
  },
  wishlist_updated: {
    compactTitle: 'Zmieniono listę',
    managerTitle: 'Zmieniono ustawienia wishlisty',
    iconLabel: 'W',
    tone: 'info',
    statusLabel: null,
  },
  wishlist_archived: {
    compactTitle: 'Zarchiwizowano listę',
    managerTitle: 'Wishlista trafiła do archiwum',
    iconLabel: 'A',
    tone: 'warning',
    statusLabel: 'Archiwum',
  },
  item_added: {
    compactTitle: 'Dodano prezent',
    managerTitle: 'Dodano prezent do wishlisty',
    iconLabel: '+',
    tone: 'success',
    statusLabel: null,
  },
  item_updated: {
    compactTitle: 'Zmieniono prezent',
    managerTitle: 'Zaktualizowano prezent',
    iconLabel: 'I',
    tone: 'info',
    statusLabel: null,
  },
  item_deleted: {
    compactTitle: 'Usunięto prezent',
    managerTitle: 'Usunięto prezent z wishlisty',
    iconLabel: '-',
    tone: 'danger',
    statusLabel: null,
  },
  reservation_started: {
    compactTitle: 'Rezerwacja oczekuje',
    managerTitle: 'Rozpoczęto rezerwację',
    iconLabel: 'R',
    tone: 'warning',
    statusLabel: 'Oczekuje na email',
  },
  reservation_confirmed: {
    compactTitle: 'Potwierdzono rezerwację',
    managerTitle: 'Rezerwacja została potwierdzona',
    iconLabel: 'OK',
    tone: 'success',
    statusLabel: 'Potwierdzona',
  },
  reservation_cancelled: {
    compactTitle: 'Anulowano rezerwację',
    managerTitle: 'Rezerwacja została anulowana',
    iconLabel: 'R',
    tone: 'danger',
    statusLabel: 'Anulowana',
  },
  share_link_created: {
    compactTitle: 'Utworzono link',
    managerTitle: 'Utworzono link publiczny',
    iconLabel: '#',
    tone: 'info',
    statusLabel: null,
  },
  share_link_rotated: {
    compactTitle: 'Odświeżono link',
    managerTitle: 'Zmieniono link publiczny',
    iconLabel: '#',
    tone: 'warning',
    statusLabel: null,
  },
  share_link_disabled: {
    compactTitle: 'Wyłączono link',
    managerTitle: 'Wyłączono link publiczny',
    iconLabel: '#',
    tone: 'danger',
    statusLabel: 'Wyłączony',
  },
  invitation_added: {
    compactTitle: 'Dodano zaproszenie',
    managerTitle: 'Dodano zaproszenie gościa',
    iconLabel: '@',
    tone: 'info',
    statusLabel: null,
  },
  invitation_removed: {
    compactTitle: 'Usunięto zaproszenie',
    managerTitle: 'Usunięto zaproszenie gościa',
    iconLabel: '@',
    tone: 'warning',
    statusLabel: null,
  },
};

export function mapWishlistActivityFeedItems(
  activities: readonly WishlistActivityViewModel[],
  mode: WishlistActivityFeedMode,
): readonly WishlistActivityFeedItemView[] {
  return activities.map((activity: WishlistActivityViewModel): WishlistActivityFeedItemView => {
    const presentation: WishlistActivityPresentation = ACTIVITY_PRESENTATION[activity.type];

    return {
      id: activity.id,
      iconLabel: presentation.iconLabel,
      title: mode === 'manager' ? presentation.managerTitle : presentation.compactTitle,
      description: createDescription(activity, mode),
      meta: formatActivityDate(activity.occurredAt),
      statusLabel: presentation.statusLabel,
      tone: presentation.tone,
    };
  });
}

function createDescription(
  activity: WishlistActivityViewModel,
  mode: WishlistActivityFeedMode,
): string {
  if (mode === 'compact') {
    return activity.wishlistTitle;
  }

  const detailParts: string[] = [];
  if (activity.itemTitle) {
    detailParts.push(activity.itemTitle);
  }

  if (activity.actorDisplayName) {
    detailParts.push(activity.actorDisplayName);
  }

  return detailParts.length > 0 ? `${activity.wishlistTitle} · ${detailParts.join(' · ')}` : activity.wishlistTitle;
}

function formatActivityDate(value: string): string {
  const date: Date | null = parseDate(value);

  return date === null ? 'Przed chwilą' : activityDateFormatter.format(date);
}

function parseDate(value: string): Date | null {
  const date: Date = new Date(value);

  return Number.isNaN(date.getTime()) ? null : date;
}
